import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Tab,
  Tabs,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from "@mui/material";
import { FilterList, Close, Refresh, FilterAlt } from "@mui/icons-material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import useInventory from "../../../hooks/useInventory";
import TabPanel from "../../../components/TabPanel";
import { a11yProps, saveFile } from "../../../utils/common";
import DatePicker from "react-multi-date-picker";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import ToolBar from "../../../components/Toolbar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Inventory</Typography>
      <Typography>Reports</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

function Reports() {
  const {
    getTransactionLogReport,
    getMaterialStockReport,
    getUserStockReport,
    getLowStockReport,
    exportInventoryReport,
  } = useInventory();

  const [tabValue, setTabValue] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");
  const dispatch = useDispatch();

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      let moduleName = "";
      if (tabValue === 0) {
        moduleName = "transaction-log";
      } else if (tabValue === 1) {
        moduleName = "warehouse-stock";
      } else if (tabValue === 2) {
        moduleName = "user-stock";
      } else if (tabValue === 3) {
        moduleName = "low-stock";
      }

      const payload = {
        filters: tabValue < 3 ? appliedFilters : [],
      };

      const resBlob = await exportInventoryReport(moduleName, fileType, payload);
      const filename = `${moduleName}_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTxnDate, setFilterTxnDate] = useState("");
  const [filterTxnType, setFilterTxnType] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");
  const [filterUserName, setFilterUserName] = useState("");
  const [filterLastIssueDate, setFilterLastIssueDate] = useState("");
  const [filterLastReturnDate, setFilterLastReturnDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setRows([]);
    setRowCount(0);
    setPaginationModel({ page: 0, pageSize: 10 });
    setAppliedFilters([]);
    setFilterTxnDate("");
    setFilterTxnType("");
    setFilterMaterialName("");
    setFilterUserName("");
    setFilterLastIssueDate("");
    setFilterLastReturnDate("");
  };

  const handleApplyFilters = () => {
    const activeFilters = [];
    if (tabValue === 0) {
      if (filterTxnDate) {
        activeFilters.push({
          fieldName: "transactionDate",
          fieldValue: filterTxnDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterTxnType) {
        activeFilters.push({
          fieldName: "transactionType",
          fieldValue: filterTxnType,
          op: "eq",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
    } else if (tabValue === 1) {
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
    } else if (tabValue === 2) {
      if (filterUserName.trim()) {
        activeFilters.push({
          fieldName: "userName",
          fieldValue: filterUserName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterMaterialName.trim()) {
        activeFilters.push({
          fieldName: "materialName",
          fieldValue: filterMaterialName.trim(),
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterLastIssueDate) {
        activeFilters.push({
          fieldName: "lastIssueDate",
          fieldValue: filterLastIssueDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      if (filterLastReturnDate) {
        activeFilters.push({
          fieldName: "lastReturnDate",
          fieldValue: filterLastReturnDate,
          op: "eq",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters(activeFilters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilterTxnDate("");
    setFilterTxnType("");
    setFilterMaterialName("");
    setFilterUserName("");
    setFilterLastIssueDate("");
    setFilterLastReturnDate("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters([]);
    setFilterOpen(false);
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    try {
      const payload = {
        pagination: {
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
        },
        filters: tabValue < 3 ? appliedFilters : [],
        sortFields: tabValue === 0 ? [{ field: "materialTransactionId", sort: "desc" }] : [],
      };

      let res;
      if (tabValue === 0) {
        res = await getTransactionLogReport(payload);
      } else if (tabValue === 1) {
        res = await getMaterialStockReport(payload);
      } else if (tabValue === 2) {
        res = await getUserStockReport(payload);
      } else if (tabValue === 3) {
        res = await getLowStockReport(payload);
      }

      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [tabValue, paginationModel, appliedFilters]);

  // Define Columns for each tab
  const getColumns = () => {
    switch (tabValue) {
      case 0:
        return [
          { field: "transactionDate", headerName: "Transaction Date", width: 170 },
          { field: "referenceNo", headerName: "Reference No", width: 160 },
          { field: "transactionType", headerName: "Transaction Type", width: 150 },
          { field: "materialName", headerName: "Material Name", width: 180 },
          { field: "inQuantity", headerName: "In Quantity", width: 120, type: "number" },
          { field: "outQuantity", headerName: "Out Quantity", width: 120, type: "number" },
          { field: "balanceQuantity", headerName: "Balance Quantity", width: 140, type: "number" },
          { field: "referenceName", headerName: "Reference Partner", width: 180 },
          { field: "remarks", headerName: "Remarks", width: 200 },
          { field: "updatedAt", headerName: "Updated At", width: 170 },
          { field: "updatedBy", headerName: "Updated By", width: 150 },
        ];
      case 1:
        return [
          { field: "materialName", headerName: "Material Name", width: 220 },
          { field: "currentQuantity", headerName: "Current Quantity", width: 160, type: "number" },
          { field: "minimumQuantity", headerName: "Minimum Quantity", width: 160, type: "number" },
          {
            field: "status",
            headerName: "Status",
            width: 150,
            renderCell: (params) => {
              const isLow = params.row.currentQuantity <= params.row.minimumQuantity;
              return (
                <span style={{ color: isLow ? "red" : "green", fontWeight: "bold" }}>
                  {isLow ? "Low Stock" : "Available"}
                </span>
              );
            },
          },
        ];
      case 2:
        return [
          { field: "userName", headerName: "User", width: 250 },
          { field: "materialName", headerName: "Material", width: 220 },
          { field: "quantity", headerName: "Available Quantity", width: 180, type: "number" },
          { field: "lastIssueDate", headerName: "Last Issue Date", width: 200 },
          { field: "lastReturnDate", headerName: "Last Return Date", width: 200 },
        ];
      case 3:
        return [
          { field: "materialName", headerName: "Material Name", width: 220 },
          { field: "currentQuantity", headerName: "Current Quantity", width: 160, type: "number" },
          { field: "minimumQuantity", headerName: "Minimum Quantity", width: 160, type: "number" },
          {
            field: "requiredQuantity",
            headerName: "Required Quantity",
            width: 180,
            type: "number",
            valueGetter: (value, row) => {
              const current = row?.currentQuantity || 0;
              const min = row?.minimumQuantity || 0;
              return Math.max(0, min - current);
            },
          },
        ];
      default:
        return [];
    }
  };

  const getRowId = (row) => {
    if (tabValue === 0) return `${row.referenceNo}-${row.materialName}-${row.transactionDate}`;
    if (tabValue === 1) return row.stockId;
    if (tabValue === 2) return `${row.userName}-${row.materialName}`;
    if (tabValue === 3) return row.stockId;
    return row.id;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Card mb={6}>
        <CardHeader title="Inventory Reports" subheader={breadCrump()} />
        <CardContent>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Inventory Reports Tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: "8px",
                },
                minHeight: "auto",
              }}
            >
              {[
                "Transaction Log",
                "Warehouse Stock Report",
                "User Stock Report",
                "Low Stock Report",
              ].map((label, idx) => (
                <Tab
                  key={idx}
                  label={label}
                  {...a11yProps(idx)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    minHeight: "40px",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    color: "text.secondary",
                    border: "1px solid",
                    borderColor: "divider",
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      borderColor: "primary.main",
                    },
                    "&:hover": {
                      backgroundColor: tabValue === idx ? "primary.main" : "action.hover",
                    },
                    transition: "all 0.2s ease",
                  }}
                />
              ))}
            </Tabs>
            <Box display="flex" gap={2} alignItems="center">
              <ToolBar
                refreshPage={fetchReportData}
                openFilterDialog={tabValue < 3 ? () => setFilterOpen(true) : undefined}
                downloadFile={handleDownloadFile}
                isLoadingDownload={isDownloading}
                downloadFileType={downloadType}
              />
            </Box>
          </Box>

          <div style={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={getColumns()}
              getRowId={getRowId}
              paginationMode="server"
              rowCount={rowCount}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={isLoading}
              disableRowSelectionOnClick
            />
          </div>

          {/* Filters Dialog */}
          <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <Typography variant="h6">Advance Filter</Typography>
              <IconButton onClick={() => setFilterOpen(false)} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3 }}>
              <Box display="flex" flexDirection="column" gap={4}>
                {/* Tab 0 – Transaction Log */}
                {tabValue === 0 && (
                  <>
                    <DatePicker
                      containerStyle={{ width: "100%" }}
                      portal
                      zIndex={10000}
                      range
                      value={filterTxnDate ? filterTxnDate.split(",") : []}
                      onChange={(dates) => {
                        if (dates && dates.length > 0) {
                          const formatted = dates.map((d) => d.format("YYYY-MM-DD")).join(",");
                          setFilterTxnDate(formatted);
                        } else {
                          setFilterTxnDate("");
                        }
                      }}
                      format="YYYY/MM/DD"
                      render={
                        <TextField
                          fullWidth
                          label="Transaction Date"
                          variant="outlined"
                          InputLabelProps={{ shrink: !!filterTxnDate }}
                        />
                      }
                    />
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="filter-txn-type-label">Transaction Type</InputLabel>
                      <Select
                        labelId="filter-txn-type-label"
                        value={filterTxnType}
                        onChange={(e) => setFilterTxnType(e.target.value)}
                        label="Transaction Type"
                      >
                        <MenuItem value="">
                          <em>All Types</em>
                        </MenuItem>
                        <MenuItem value="OPENING">Opening</MenuItem>
                        <MenuItem value="PURCHASE">Purchase</MenuItem>
                        <MenuItem value="ISSUE">Issue</MenuItem>
                        <MenuItem value="RETURN">Return</MenuItem>
                        <MenuItem value="SALE">Sale</MenuItem>
                        <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Material Name"
                      variant="outlined"
                      value={filterMaterialName}
                      onChange={(e) => setFilterMaterialName(e.target.value)}
                      placeholder="e.g. Fiber, ONU"
                      fullWidth
                    />
                  </>
                )}

                {/* Tab 1 – Warehouse Stock */}
                {tabValue === 1 && (
                  <TextField
                    label="Material Name"
                    variant="outlined"
                    value={filterMaterialName}
                    onChange={(e) => setFilterMaterialName(e.target.value)}
                    placeholder="e.g. Fiber, ONU"
                    fullWidth
                  />
                )}

                {/* Tab 2 – User Stock */}
                {tabValue === 2 && (
                  <>
                    <TextField
                      label="Username"
                      variant="outlined"
                      value={filterUserName}
                      onChange={(e) => setFilterUserName(e.target.value)}
                      placeholder="e.g. john_doe"
                      fullWidth
                    />
                    <TextField
                      label="Material Name"
                      variant="outlined"
                      value={filterMaterialName}
                      onChange={(e) => setFilterMaterialName(e.target.value)}
                      placeholder="e.g. Fiber, ONU"
                      fullWidth
                    />
                    <DatePicker
                      containerStyle={{ width: "100%" }}
                      portal
                      zIndex={10000}
                      value={filterLastIssueDate}
                      onChange={(date) =>
                        setFilterLastIssueDate(date ? date.format("YYYY-MM-DD") : "")
                      }
                      format="YYYY/MM/DD"
                      render={
                        <TextField
                          fullWidth
                          label="Last Issue Date"
                          variant="outlined"
                          InputLabelProps={{ shrink: !!filterLastIssueDate }}
                        />
                      }
                    />
                    <DatePicker
                      containerStyle={{ width: "100%" }}
                      portal
                      zIndex={10000}
                      value={filterLastReturnDate}
                      onChange={(date) =>
                        setFilterLastReturnDate(date ? date.format("YYYY-MM-DD") : "")
                      }
                      format="YYYY/MM/DD"
                      render={
                        <TextField
                          fullWidth
                          label="Last Return Date"
                          variant="outlined"
                          InputLabelProps={{ shrink: !!filterLastReturnDate }}
                        />
                      }
                    />
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleClearFilters} variant="outlined" startIcon={<Refresh />}>
                Reset
              </Button>
              <Button
                onClick={handleApplyFilters}
                variant="contained"
                color="primary"
                startIcon={<FilterAlt />}
              >
                filter
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Reports;
